import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '../_services/auth/auth.service';
import { InstitutionInvitationService } from '../_services/institution-invitation.service';

export const authGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const invitationService = inject(InstitutionInvitationService);
  const router = inject(Router);

  const requiresAuth = route.data['requiresAuth'] !== false;
  const hasToken = authService.isLoggedIn();
  const tokenExpired = authService.isTokenExpired();

  // An expired token is purged on every route, not just auth-required ones.
  // Otherwise the cookie/signal stay populated on /login and the layout
  // ends up showing a logout button on the login screen.
  if (hasToken && tokenExpired) {
    authService.logout();
    return false;
  }

  const effectivelyLoggedIn = hasToken && !tokenExpired;

  if (requiresAuth && !effectivelyLoggedIn) {
    router.navigate(['/login']);
    return false;
  }

  if (!requiresAuth && effectivelyLoggedIn) {
    // A teacher already logged in to NT2 who follows an invitation link lands
    // on /login?invitation=<id>. Complete the pending invitation for the
    // session here instead of dropping it on the bounce to /licenses (DUNX-88).
    const invitationId = route.queryParamMap.get('invitation');
    if (invitationId) {
      return invitationService.complete(invitationId).pipe(
        map((response) => response?.redirectTo ?? '/licenses'),
        catchError(() => of('/licenses')),
        // Clear on every outcome (200/204/error) so a stale id can't leak into
        // a later login flow, which falls back to getId() without a query param.
        map((target) => {
          invitationService.clear();
          return router.parseUrl(target);
        }),
      );
    }

    router.navigate(['/licenses']);
    return false;
  }

  return true;
};
